<template>
  <div class="modal-overlay" @click.self="closeModal">
    <div class="modal-content">
      <div class="modal-header">
        <h2>Ajustes</h2>
        <button @click="closeModal" class="close-btn" title="Close">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body">
        <section class="settings-section">
          <h3>Tableros por Defecto</h3>
          <p class="section-description">
            Estos tableros aparecerán siempre, incluso cuando se elimine una
            emergencia.
          </p>

          <div class="default-boards-list">
            <div
              v-for="board in defaultBoards"
              :key="board.name"
              class="board-item"
            >
              <div class="board-info">
                <input
                  v-if="board.isEditing"
                  v-model="board.editingName"
                  @keyup.enter="saveBoard(board)"
                  @keyup.esc="cancelEdit(board)"
                  class="board-name-input"
                  placeholder="Nombre del tablero"
                  maxlength="50"
                  ref="editInput"
                />
                <span v-else class="board-name" @click="startEdit(board)">
                  {{ board.name }}
                </span>
                <span v-if="board.isPreset" class="board-tag"
                  >Personal disponible</span
                >
              </div>
              <div class="board-actions">
                <button
                  v-if="board.isEditing"
                  @click="saveBoard(board)"
                  class="save-btn"
                  title="Guardar cambios"
                >
                  <span class="material-icons">check</span>
                </button>
                <button
                  v-if="board.isEditing"
                  @click="cancelEdit(board)"
                  class="cancel-btn"
                  title="Cancelar edición"
                >
                  <span class="material-icons">close</span>
                </button>
                <button
                  v-if="!board.isEditing"
                  @click="startEdit(board)"
                  class="edit-btn"
                  title="Editar nombre"
                >
                  <span class="material-icons">edit</span>
                </button>
                <button
                  v-if="!board.isEditing"
                  @click="removeBoard(board.name)"
                  class="remove-btn"
                  title="Eliminar tablero por defecto"
                >
                  <span class="material-icons">delete</span>
                </button>
              </div>
            </div>
          </div>

          <div class="add-board-form">
            <h4>Agregar Tablero por Defecto</h4>
            <div class="form-group">
              <label for="boardName">Nombre del Tablero:</label>
              <input
                id="boardName"
                v-model="newBoardName"
                type="text"
                placeholder="Ej: Alfa, Bravo, Charlie..."
                maxlength="50"
              />
            </div>
            <button
              @click="addBoard"
              :disabled="!newBoardName.trim()"
              class="add-board-btn"
            >
              Agregar Tablero
            </button>
          </div>
        </section>

        <section class="settings-section">
          <h3>Compañía por Defecto</h3>
          <p class="section-description">
            Esta compañía se rellenará automáticamente al agregar nuevo
            personal.
          </p>

          <!-- Show form when no default station exists -->
          <div v-if="!currentDefaultStation" class="default-station-form">
            <div class="form-group">
              <label for="defaultStation">Compañía por Defecto:</label>
              <input
                id="defaultStation"
                v-model="defaultStation"
                @keyup.enter="saveDefaultStation"
                type="text"
                placeholder="Ej: 1 cía, 2 cía, 3 cía..."
                maxlength="50"
              />
            </div>
            <button
              @click="saveDefaultStation"
              :disabled="!defaultStation.trim()"
              class="add-station-btn"
            >
              <span class="material-icons">add</span>
              Agregar Compañía
            </button>
          </div>

          <!-- Show current station with edit/delete options -->
          <div v-else class="current-station-display">
            <div class="station-item">
              <div class="station-info">
                <input
                  v-if="isEditingStation"
                  v-model="editingStationName"
                  @keyup.enter="saveStationEdit"
                  @keyup.esc="cancelStationEdit"
                  class="station-name-input"
                  placeholder="Nombre de la compañía"
                  maxlength="50"
                  ref="editStationInput"
                />
                <span v-else class="station-name">
                  {{ currentDefaultStation }}
                </span>
              </div>
              <div class="station-actions">
                <button
                  v-if="isEditingStation"
                  @click="saveStationEdit"
                  class="save-btn"
                  title="Guardar cambios"
                >
                  <span class="material-icons">check</span>
                </button>
                <button
                  v-if="isEditingStation"
                  @click="cancelStationEdit"
                  class="cancel-btn"
                  title="Cancelar edición"
                >
                  <span class="material-icons">close</span>
                </button>
                <button
                  v-if="!isEditingStation"
                  @click="startEditStation"
                  class="edit-btn"
                  title="Editar compañía"
                >
                  <span class="material-icons">edit</span>
                </button>
                <button
                  v-if="!isEditingStation"
                  @click="deleteDefaultStation"
                  class="remove-btn"
                  title="Eliminar compañía por defecto"
                >
                  <span class="material-icons">delete</span>
                </button>
              </div>
            </div>
          </div>
        </section>

        <section class="settings-section">
          <h3>Configuración de Cronómetros</h3>
          <p class="section-description">
            Configura los tiempos y colores de los cronómetros del personal.
          </p>

          <div class="timer-config-form">
            <div class="form-group">
              <label for="greenThreshold">Umbral Verde (minutos):</label>
              <input
                id="greenThreshold"
                v-model.number="timerConfig.greenThreshold"
                type="number"
                min="1"
                :max="timerConfig.yellowThreshold - 1"
                placeholder="15"
              />
              <small>Tiempo en verde desde 0 hasta este valor</small>
            </div>

            <div class="form-group">
              <label for="yellowThreshold">Umbral Amarillo (minutos):</label>
              <input
                id="yellowThreshold"
                v-model.number="timerConfig.yellowThreshold"
                type="number"
                :min="timerConfig.greenThreshold + 1"
                :max="timerConfig.maxTime - 1"
                placeholder="25"
              />
              <small
                >Tiempo en amarillo desde {{ timerConfig.greenThreshold }} hasta
                este valor</small
              >
            </div>

            <div class="form-group">
              <label for="maxTime">Tiempo Máximo (minutos):</label>
              <input
                id="maxTime"
                v-model.number="timerConfig.maxTime"
                type="number"
                min="1"
                max="180"
                placeholder="40"
              />
              <small>
                Tiempo máximo para la barra de progreso. El cronómetro
                continuará independientemente de este valor.</small
              >
            </div>

            <button
              v-if="timerConfigChanged"
              @click="saveTimerConfig"
              class="save-timer-btn"
            >
              Guardar Configuración
            </button>
          </div>
        </section>

        <section class="settings-section danger-section">
          <h3>Zona de Peligro</h3>
          <p class="section-description">
            Estas acciones no se pueden deshacer. Ten cuidado al usarlas.
          </p>

          <button @click="resetApp" class="reset-btn">
            <span class="material-icons">refresh</span>
            Resetear Aplicación
          </button>
          <p class="reset-description">
            Esto eliminará toda la información: emergencias, tableros por
            defecto y configuraciones. Solo se mantendrá el tablero "Personal
            disponible".
          </p>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SettingsModal',
  props: {
    taskList: {
      type: Object,
      default: null,
    },
    notes: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      newBoardName: '',
      defaultBoards: [],
      defaultStation: '',
      isEditingStation: false,
      editingStationName: '',
      timerConfig: {
        maxTime: 40,
        greenThreshold: 15,
        yellowThreshold: 25,
      },
      originalTimerConfig: {
        maxTime: 40,
        greenThreshold: 15,
        yellowThreshold: 25,
      },
    }
  },
  computed: {
    currentDefaultStation() {
      if (this.taskList && this.taskList.getDefaultStation) {
        return this.taskList.getDefaultStation()
      }
      return ''
    },
    timerConfigChanged() {
      return (
        this.timerConfig.maxTime !== this.originalTimerConfig.maxTime ||
        this.timerConfig.greenThreshold !==
          this.originalTimerConfig.greenThreshold ||
        this.timerConfig.yellowThreshold !==
          this.originalTimerConfig.yellowThreshold
      )
    },
  },
  emits: ['close'],
  methods: {
    closeModal() {
      this.$emit('close')
    },
    loadDefaultBoards() {
      if (this.taskList && this.taskList.getDefaultBoards) {
        this.defaultBoards = this.taskList.getDefaultBoards().map((board) => ({
          ...board,
          isEditing: false,
          editingName: '',
        }))
      }
    },
    loadDefaultStation() {
      if (this.taskList && this.taskList.getDefaultStation) {
        this.defaultStation = this.taskList.getDefaultStation()
      }
    },
    saveDefaultStation() {
      if (!this.defaultStation.trim()) return

      if (this.taskList && this.taskList.setDefaultStation) {
        this.taskList.setDefaultStation(this.defaultStation.trim())
        this.defaultStation = '' // Clear the input after saving
      }
    },
    clearDefaultStation() {
      if (this.taskList && this.taskList.clearDefaultStation) {
        this.taskList.clearDefaultStation()
        this.defaultStation = ''
      }
    },
    startEditStation() {
      this.isEditingStation = true
      this.editingStationName = this.currentDefaultStation

      this.$nextTick(() => {
        // Focus the input field
        const input = this.$refs.editStationInput
        if (input) {
          input.focus()
          input.select()
        }
      })
    },
    saveStationEdit() {
      if (!this.editingStationName.trim()) {
        alert('El nombre de la compañía no puede estar vacío')
        return
      }

      if (this.taskList && this.taskList.setDefaultStation) {
        this.taskList.setDefaultStation(this.editingStationName.trim())
        this.isEditingStation = false
        this.editingStationName = ''
      }
    },
    cancelStationEdit() {
      this.isEditingStation = false
      this.editingStationName = ''
    },
    deleteDefaultStation() {
      if (
        confirm(
          `¿Está seguro de que desea eliminar la compañía por defecto "${this.currentDefaultStation}"?`
        )
      ) {
        if (this.taskList && this.taskList.clearDefaultStation) {
          this.taskList.clearDefaultStation()
        }
      }
    },
    loadTimerConfig() {
      if (this.taskList && this.taskList.getTimerConfig) {
        const config = this.taskList.getTimerConfig()
        const timerData = {
          maxTime: Math.floor(config.maxTime / (60 * 1000)), // Convert ms to minutes
          greenThreshold: Math.floor(
            config.colorThresholds[0].time / (60 * 1000)
          ),
          yellowThreshold: Math.floor(
            config.colorThresholds[1].time / (60 * 1000)
          ),
        }
        this.timerConfig = { ...timerData }
        this.originalTimerConfig = { ...timerData }
      }
    },
    saveTimerConfig() {
      if (this.taskList && this.taskList.setTimerConfig) {
        const config = {
          maxTime: this.timerConfig.maxTime * 60 * 1000, // Convert minutes to ms
          colorThresholds: [
            {
              time: this.timerConfig.greenThreshold * 60 * 1000,
              color: '#2dce89',
            },
            {
              time: this.timerConfig.yellowThreshold * 60 * 1000,
              color: '#ffd600',
            },
            { time: Infinity, color: '#ed4245' },
          ],
        }
        this.taskList.setTimerConfig(config)
        // Update original config to reflect saved state
        this.originalTimerConfig = { ...this.timerConfig }
      }
    },
    startEdit(board) {
      // Cancel any other editing boards first
      this.defaultBoards.forEach((b) => {
        if (b.isEditing) {
          this.cancelEdit(b)
        }
      })

      board.isEditing = true
      board.editingName = board.name

      this.$nextTick(() => {
        // Focus the input field
        const inputs = this.$refs.editInput
        if (inputs) {
          const input = Array.isArray(inputs)
            ? inputs[inputs.length - 1]
            : inputs
          input?.focus()
          input?.select()
        }
      })
    },
    saveBoard(board) {
      if (!board.editingName.trim()) {
        alert('El nombre del tablero no puede estar vacío')
        return
      }

      // Check if name already exists (excluding current board)
      const exists = this.defaultBoards.some(
        (b) =>
          b.name.toLowerCase() === board.editingName.trim().toLowerCase() &&
          b.name !== board.name
      )

      if (exists) {
        alert('Ya existe un tablero con ese nombre')
        return
      }

      if (this.taskList && this.taskList.updateDefaultBoard) {
        this.taskList.updateDefaultBoard(board.name, board.editingName.trim())
        board.name = board.editingName.trim()
        board.isEditing = false
        board.editingName = ''
      }
    },
    cancelEdit(board) {
      board.isEditing = false
      board.editingName = ''
    },
    addBoard() {
      if (!this.newBoardName.trim()) return

      // Check if board name already exists
      const exists = this.defaultBoards.some(
        (board) =>
          board.name.toLowerCase() === this.newBoardName.trim().toLowerCase()
      )

      if (exists) {
        alert('Ya existe un tablero con ese nombre')
        return
      }

      if (this.taskList && this.taskList.addDefaultBoard) {
        this.taskList.addDefaultBoard({
          name: this.newBoardName.trim(),
        })
        this.loadDefaultBoards()
        this.newBoardName = ''
      }
    },
    removeBoard(boardName) {
      if (
        confirm(
          `¿Está seguro de que desea quitar "${boardName}" de los tableros por defecto? El tablero actual se mantendrá pero no aparecerá en futuras emergencias.`
        )
      ) {
        if (this.taskList && this.taskList.removeDefaultBoard) {
          this.taskList.removeDefaultBoard(boardName)
          this.loadDefaultBoards()
        }
      }
    },
    resetApp() {
      const confirmMessage = `¿Está completamente seguro de que desea resetear toda la aplicación?`

      if (confirm(confirmMessage)) {
        if (this.taskList && this.taskList.resetApp) {
          this.taskList.resetApp()
          this.loadDefaultBoards()
          this.loadDefaultStation()
          this.loadTimerConfig()
        }
        if (this.notes && this.notes.resetNotes) {
          this.notes.resetNotes()
        }
      }
    },
  },
  mounted() {
    // Add listener to close modal on Escape key press
    this.escapeListener = (e) => {
      if (e.key === 'Escape') {
        this.closeModal()
      }
    }
    document.addEventListener('keydown', this.escapeListener)

    // Load default boards and station when modal opens
    this.loadDefaultBoards()
    this.loadDefaultStation()
    this.loadTimerConfig()
  },
  beforeUnmount() {
    // Clean up the event listener when the component is destroyed
    document.removeEventListener('keydown', this.escapeListener)
  },
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background-color: #2f3136;
  padding: 24px;
  border-radius: 8px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #4f545c;
  padding-bottom: 16px;
  margin-bottom: 16px;
}

.modal-header h2 {
  margin: 0;
  color: #ffffff;
}

.close-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #b9bbbe;
  padding: 4px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
}

.close-btn:hover {
  background-color: #4f545c;
}

.settings-section {
  margin-bottom: 24px;
}

.settings-section h3 {
  color: #ffffff;
  margin: 0 0 8px 0;
  font-size: 18px;
}

.danger-section {
  border: 1px solid #ed4245;
  border-radius: 8px;
  padding: 16px;
  background-color: rgba(237, 66, 69, 0.05);
}

.danger-section h3 {
  color: #ed4245;
  display: flex;
  align-items: center;
  gap: 8px;
}

.danger-section h3::before {
  content: '⚠️';
  font-size: 16px;
}

.section-description {
  color: #b9bbbe;
  margin: 0 0 16px 0;
  font-size: 14px;
  line-height: 1.4;
}

.default-boards-list {
  margin-bottom: 20px;
}

.board-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background-color: #40444b;
  border-radius: 6px;
  margin-bottom: 8px;
}

.board-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}

.board-name {
  font-weight: 600;
  color: #ffffff;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.board-name:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.board-name-input {
  background-color: #202225;
  border: 1px solid #5865f2;
  border-radius: 4px;
  padding: 4px 8px;
  color: #ffffff;
  font-weight: 600;
  font-size: 14px;
  outline: none;
}

.board-name-input::placeholder {
  color: #72767d;
}

.board-tag {
  font-size: 11px;
  background-color: #5865f2;
  color: white;
  padding: 2px 6px;
  border-radius: 10px;
  align-self: flex-start;
}

.board-actions {
  display: flex;
  gap: 4px;
  align-items: center;
}

.edit-btn,
.save-btn,
.cancel-btn,
.remove-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
  width: 32px;
  height: 32px;
}

.edit-btn {
  color: #b9bbbe;
}

.edit-btn:hover {
  background-color: rgba(185, 187, 190, 0.1);
}

.save-btn {
  color: #2dce89;
}

.save-btn:hover {
  background-color: rgba(45, 206, 137, 0.1);
}

.cancel-btn {
  color: #b9bbbe;
}

.cancel-btn:hover {
  background-color: rgba(185, 187, 190, 0.1);
}

.remove-btn {
  color: #ed4245;
}

.remove-btn:hover {
  background-color: rgba(237, 66, 69, 0.1);
}

.add-board-form {
  background-color: #40444b;
  padding: 16px;
  border-radius: 8px;
}

.add-board-form h4 {
  margin: 0 0 12px 0;
  color: #ffffff;
  font-size: 16px;
}

.form-group {
  margin-bottom: 12px;
}

.form-group label {
  display: block;
  margin-bottom: 4px;
  color: #b9bbbe;
  font-size: 14px;
  font-weight: 500;
}

.form-group input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #202225;
  border-radius: 4px;
  background-color: #202225;
  color: #dcddde;
  font-size: 14px;
  box-sizing: border-box;
  transition: border-color 0.2s;
}

.form-group input:focus {
  border-color: #5865f2;
  outline: none;
}

.form-group input::placeholder {
  color: #72767d;
}

.add-board-btn {
  background-color: #2dce89;
  color: white;
  border: none;
  padding: 10px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  transition: background-color 0.2s;
  width: 100%;
}

.add-board-btn:hover:not(:disabled) {
  background-color: #1d9863;
}

.add-board-btn:disabled {
  background-color: #4f545c;
  cursor: not-allowed;
  opacity: 0.6;
}

/* Default Station Styles */
.default-station-form {
  background-color: #40444b;
  border-radius: 6px;
  padding: 16px;
}

.add-station-btn {
  background-color: #2dce89;
  color: white;
  border: none;
  padding: 10px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  transition: background-color 0.2s;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 12px;
}

.add-station-btn:hover:not(:disabled) {
  background-color: #1d9863;
}

.add-station-btn:disabled {
  background-color: #4f545c;
  cursor: not-allowed;
  opacity: 0.6;
}

.current-station-display {
  background-color: #40444b;
  border-radius: 6px;
  padding: 16px;
}

.station-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background-color: #2f3136;
  border-radius: 6px;
  border-left: 3px solid #2dce89;
}

.station-info {
  flex: 1;
  margin-right: 12px;
}

.station-name {
  color: #ffffff;
  font-weight: 600;
  font-size: 16px;
}

.station-name-input {
  background-color: #202225;
  border: 1px solid #4f545c;
  border-radius: 4px;
  padding: 8px 12px;
  color: #dcddde;
  font-size: 16px;
  width: 100%;
  box-sizing: border-box;
}

.station-name-input:focus {
  background-color: #36393f;
  border-color: #5865f2;
  outline: none;
}

.station-actions {
  display: flex;
  gap: 8px;
}

.reset-btn {
  background-color: #ed4245;
  color: white;
  border: none;
  padding: 12px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.reset-btn:hover {
  background-color: #c43a3d;
}

.reset-btn .material-icons {
  font-size: 20px;
}

.reset-description {
  color: #b9bbbe;
  font-size: 12px;
  line-height: 1.4;
  margin: 0;
  font-style: italic;
}

/* Timer Configuration Styles */
.timer-config-form {
  background-color: #40444b;
  border-radius: 8px;
  padding: 16px;
}

.timer-input-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.timer-input-row label {
  color: #b9bbbe;
  font-size: 14px;
  font-weight: 500;
  min-width: 140px;
}

.timer-input-row input {
  padding: 8px 12px;
  border: 1px solid #202225;
  border-radius: 4px;
  background-color: #202225;
  color: #dcddde;
  font-size: 14px;
  width: 80px;
  text-align: center;
  transition: border-color 0.2s;
}

.timer-input-row input:focus {
  border-color: #5865f2;
  outline: none;
}

.timer-input-row .color-indicator {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid #36393f;
  flex-shrink: 0;
}

.readonly-field {
  padding: 8px 12px;
  border: 1px solid #4f545c;
  border-radius: 4px;
  background-color: #36393f;
  color: #b9bbbe;
  font-size: 14px;
  font-style: italic;
  min-width: 120px;
  text-align: center;
}

.timer-config-actions {
  display: flex;
  gap: 12px;
  margin-top: 16px;
  justify-content: flex-end;
}

.save-timer-btn {
  background-color: #2dce89;
  color: white;
  border: none;
  padding: 10px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  transition: background-color 0.2s;
}

.save-timer-btn:hover:not(:disabled) {
  background-color: #1d9863;
}

.save-timer-btn:disabled {
  background-color: #4f545c;
  cursor: not-allowed;
  opacity: 0.6;
}

.reset-timer-btn {
  background-color: #4f545c;
  color: #dcddde;
  border: none;
  padding: 10px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  transition: background-color 0.2s;
}

.reset-timer-btn:hover {
  background-color: #5d6269;
}

@media (max-width: 768px) {
  .modal-content {
    width: 95%;
    max-height: 90vh;
    padding: 16px;
  }

  .board-item {
    padding: 10px;
  }

  .add-board-form {
    padding: 12px;
  }

  .timer-config-form {
    padding: 12px;
  }

  .timer-input-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .timer-input-row label {
    min-width: auto;
  }

  .timer-config-actions {
    flex-direction: column;
  }

  .save-timer-btn,
  .reset-timer-btn {
    width: 100%;
  }
}
</style>
